import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import globalStyles from '../globals/pages.module.scss'

import Footer from '../components/layout/footer/footer'
import AppTopBar from '../components/layout/header/header'
import { useParams } from 'react-router-dom'
import {
    useAuthUserData,
    useSetUserOrganisations,
    useUpdateAuthUserData,
    useSetPcpReferralTeamLeader,
} from '../authUserProvider'
import { IsMobile } from '../components/layout/isMobile/isMobile'
import { useBranding } from '../branding/useBranding'
import { normalisePageOverrides } from '../access/pageAccess'

declare global {
    interface Window {
        dataLayer: any
    }
}
function Main() {
    const updateAuthUser = useUpdateAuthUserData()
    const setUserOrganisations = useSetUserOrganisations()
    const setTeamLeader = useSetPcpReferralTeamLeader()
    const authUser = useAuthUserData()
    const [lock, setLock] = useState(false)
    const isMobile = IsMobile()
    const brand = useBranding()

    // Apply the white-label theme globally: the `data-brand` attribute flips the
    // CSS-variable colour theme (src/globals/theme.scss), and the title/favicon
    // follow the active org's brand. Reverts to Sentinel defaults when the org
    // resolves back to Sentinel.
    useEffect(() => {
        document.documentElement.setAttribute('data-brand', brand.dataBrand)
        document.title = brand.title

        let favicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
        if (!favicon) {
            favicon = document.createElement('link')
            favicon.rel = 'icon'
            document.head.appendChild(favicon)
        }
        favicon.href = brand.faviconHref
    }, [brand])

    useEffect(() => {
        const userString = window.localStorage.getItem('user')
        if (userString) updateAuthUser(JSON.parse(userString))

        // Rehydrate the multi-org list on reload — auth.tsx stashes it here
        // after /init. Without this, a hard refresh (or a new tab opened via
        // window.open) would empty the sidebar switcher and lock multi-org
        // users out of their extras' routes. Read from localStorage so new
        // tabs inherit the session rather than 404ing on gated routes.
        const orgsString = window.localStorage.getItem('userOrganisations')
        if (orgsString) {
            try {
                const parsed = JSON.parse(orgsString)
                if (parsed && Array.isArray(parsed.organisations)) {
                    setUserOrganisations({
                        primary: parsed.primary || '',
                        organisations: parsed.organisations,
                        pageOverridesByOrg: normalisePageOverrides(parsed.pageOverridesByOrg)
                    })
                }
            } catch {
                // Stale/corrupt value — ignore and let the next /init refresh it.
            }
        }

        // Rehydrate PCP Referral team-leader status on reload (set by auth.tsx
        // after /init) so the Team Leader page + sidebar link survive a refresh.
        const tlString = window.localStorage.getItem('pcpReferralTeamLeader')
        if (tlString) {
            try {
                const parsed = JSON.parse(tlString)
                if (parsed && typeof parsed.isLeader === 'boolean') {
                    setTeamLeader({
                        isLeader: parsed.isLeader,
                        teamId: parsed.teamId ?? null,
                        teamName: parsed.teamName ?? null,
                    })
                }
            } catch {
                // Stale/corrupt value — ignore and let the next /init refresh it.
            }
        }
    }, [])
    useEffect(() => {
        if (typeof authUser != 'undefined') {
            setLock(authUser.sidebar)
        }
    }, [authUser])

    const location = useLocation()

    const HIDE_FOOTER_PATHS = ['/military-hearing-loss/add-client']
    const hideFooter = HIDE_FOOTER_PATHS.some((p) => location.pathname.startsWith(p))

    return (
        <div className={`${lock ? globalStyles.sidebarLock : ''}  main`}>
            {isMobile && <AppTopBar />}
            <Outlet />
            {!hideFooter && <Footer />}
        </div>
    )
}

export default Main
